import React from 'react';
import { CheckCircle2, Film, Play } from 'lucide-react';
import { getHelpVideoPublicUrl } from '@/config/environment-config';
import { cn } from '@/lib/utils';
import { formatDuration, kindLabel } from './help-gallery';
import type { HelpEntry } from './help-types';

export interface HelpTileProps {
  entry: HelpEntry;
  watched: boolean;
  onPlay: (entry: HelpEntry) => void;
  /** The one-sentence purpose under the title (Help page); the Help sheet leaves it out. */
  showPurpose?: boolean;
  /** Marks the tile that is playing now (Up next list, Help sheet). */
  active?: boolean;
  /** Gives the tile the entry id, the target of deep links (`/help/sop#sop-goniometer`). */
  anchor?: boolean;
  className?: string;
}

/**
 * One Help entry as a tile (Q48): poster frame, length, a Flow or Clip badge (task videos
 * need none) and a watched tick, with the title under it. Choosing it plays the video in
 * the shared player. Entries without a video are drawn quieter, marked "Coming soon"; their
 * Interim steps open in the player.
 */
export function HelpTile({
  entry,
  watched,
  onPlay,
  showPurpose = false,
  active = false,
  anchor = false,
  className,
}: HelpTileProps) {
  const poster = entry.poster ? getHelpVideoPublicUrl(entry.poster) : null;
  const published = entry.video !== null;
  const kind = kindLabel(entry);
  return (
    <button
      type="button"
      id={anchor ? entry.id : undefined}
      onClick={() => onPlay(entry)}
      aria-current={active ? 'true' : undefined}
      data-testid={`help-tile-${entry.id}`}
      className={cn(
        'group flex w-full min-w-0 scroll-mt-24 flex-col gap-2 rounded-xl p-1.5 text-left transition-colors',
        'hover:bg-accent/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        active && 'bg-accent ring-1 ring-primary/40',
        className,
      )}
    >
      <span
        className={cn(
          'relative block aspect-video w-full overflow-hidden rounded-lg border bg-muted',
          published ? 'border-border shadow-sm' : 'border-dashed border-border/80 bg-muted/50',
        )}
      >
        {poster ? (
          <img
            src={poster}
            alt=""
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.02] motion-reduce:transition-none motion-reduce:group-hover:scale-100"
          />
        ) : (
          <span className="flex h-full w-full items-center justify-center text-muted-foreground/70">
            <Film className="h-7 w-7" aria-hidden />
          </span>
        )}
        {published && (
          <span className="absolute inset-0 flex items-center justify-center transition-colors group-hover:bg-black/15">
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-black/60 text-white shadow-md transition-transform group-hover:scale-110 motion-reduce:transition-none">
              <Play className="ml-0.5 h-5 w-5 fill-current" aria-hidden />
            </span>
          </span>
        )}
        {kind !== 'Task' && (
          <span
            className={cn(
              'absolute left-1.5 top-1.5 rounded px-1.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide',
              kind === 'Flow' ? 'bg-primary text-primary-foreground' : 'bg-black/70 text-white',
            )}
          >
            {kind}
          </span>
        )}
        {published ? (
          entry.durationSec !== null && (
            <span className="absolute bottom-1.5 right-1.5 rounded bg-black/75 px-1.5 py-0.5 text-xs font-medium tabular-nums text-white">
              {formatDuration(entry.durationSec)}
            </span>
          )
        ) : (
          <span className="absolute bottom-1.5 right-1.5 rounded border border-border bg-background/90 px-1.5 py-0.5 text-xs font-medium text-muted-foreground">
            Coming soon
          </span>
        )}
      </span>
      <span className="flex items-start gap-1.5 px-0.5">
        <span className="min-w-0 flex-1">
          <span className={cn('line-clamp-2 text-sm font-medium leading-snug', published ? 'text-foreground' : 'text-muted-foreground')}>
            {entry.title}
          </span>
          {showPurpose && entry.purpose && (
            <span className="mt-1 line-clamp-3 text-xs leading-snug text-muted-foreground">{entry.purpose}</span>
          )}
        </span>
        {watched && <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-label="Watched" role="img" />}
      </span>
    </button>
  );
}
